/**
 * Audit-Trail Explorer (PR_RUN_037)
 * ==================================
 * Single-pane filter UI over the consolidated `operational_event_logs`
 * collection (built in PR_RUN_033). Admin / auditor only.
 *
 * Filters: source (6 chips) · action (text) · actor user_id (text) ·
 *          entity id (text) · since (date) · until (date) · page size.
 *
 * Table: timestamp · source pill · action · actor · entity · payload preview.
 * Row expand reveals the full payload JSON with a click-to-copy affordance.
 *
 * Pagination: prev/next with cursor offset; total count from the API.
 *
 * Authored: 2026-05-23 (E1, PR_RUN_037)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import {
  Filter, ChevronLeft, ChevronRight, RefreshCw, Search,
  Eye, Activity, AlertTriangle, X, Copy, Download, BookmarkPlus, Bookmark, Trash2,
} from "lucide-react";

const PAGE_SIZE = 50;

const SOURCE_META = {
  audit_log:    { label: "Audit Log",    color: "#FBBF24", legacy: "audit_log (legacy)" },
  audit_trail:  { label: "Audit Trail",  color: "#A78BFA", legacy: "audit_trail" },
  governance:   { label: "Governance",   color: "#38BDF8", legacy: "governance_activity_logs" },
  remediation:  { label: "Remediation",  color: "#F87171", legacy: "remediation_activity_logs" },
  intelligence: { label: "Intelligence", color: "#10B981", legacy: "intelligence_logs" },
  model_run:    { label: "Model Run",    color: "#C9A84C", legacy: "model_run_logs" },
};

const fmtTimestamp = (ts) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

const SourcePill = ({ source }) => {
  const meta = SOURCE_META[source] || { label: source, color: "#8B95A5" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] rounded-full font-mono font-medium border"
      style={{
        color: meta.color,
        borderColor: `${meta.color}40`,
        background: `${meta.color}12`,
      }}
      title={meta.legacy ? `Migrated from ${meta.legacy} (PR_RUN_033)` : null}
    >
      {meta.label}
    </span>
  );
};

const AuditExplorerPage = () => {
  const { user, isAdmin } = useAuth();
  const isAuditor = user?.role === "auditor";
  const canAccess = isAdmin || isAuditor;

  // Filters
  const [source, setSource] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [actorId, setActorId] = useState("");
  const [entityId, setEntityId] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  // Data
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState([]);
  const [expanded, setExpanded] = useState({});

  // PR_RUN_040 — saved searches
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [exporting, setExporting] = useState(false);

  // PR_RUN_050 — pre-fill from URL query params on mount so deep-links
  // from TrialFunnelPage (e.g. /audit-explorer?source=trial&action=signup)
  // land with their filters already applied. Runs once on mount.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlSource = params.get("source");
      const urlAction = params.get("action");
      const urlActor = params.get("actor_id");
      const urlEntity = params.get("entity_id");
      const urlSince = params.get("since");
      const urlUntil = params.get("until");
      const urlQ = params.get("q");
      if (urlSource) setSource(urlSource);
      if (urlAction) setActionFilter(urlAction);
      if (urlActor) setActorId(urlActor);
      if (urlEntity) setEntityId(urlEntity);
      // Accept either ISO timestamp or YYYY-MM-DD; the <input type="date">
      // only renders the latter, so we slice off the time portion.
      if (urlSince) setSince(urlSince.slice(0, 10));
      if (urlUntil) setUntil(urlUntil.slice(0, 10));
      if (urlQ) setQ(urlQ);
    } catch { /* ignore parse failures, SSR safety */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSources = useCallback(async () => {
    try {
      const r = await fetch(`${API}/audit-explorer/sources`, { credentials: "include" });
      if (r.ok) {
        const j = await r.json();
        setSources(j.sources || []);
      }
    } catch (e) { /* non-fatal */ }
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (source) params.set("source", source);
      if (actionFilter.trim()) params.set("action", actionFilter.trim());
      if (actorId.trim()) params.set("actor_id", actorId.trim());
      if (entityId.trim()) params.set("entity_id", entityId.trim());
      if (since) {
        // <input type="date"> gives YYYY-MM-DD; convert to ISO start-of-day UTC.
        params.set("since", `${since}T00:00:00+00:00`);
      }
      if (until) {
        params.set("until", `${until}T23:59:59+00:00`);
      }
      if (q.trim()) params.set("q", q.trim());
      const r = await fetch(`${API}/audit-explorer/events?${params.toString()}`, {
        credentials: "include",
      });
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        setEvents([]);
        setTotal(0);
        return;
      }
      const j = await r.json();
      setEvents(j.items || []);
      setTotal(j.total || 0);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [source, actionFilter, actorId, entityId, since, until, q, offset]);

  useEffect(() => { if (canAccess) fetchSources(); }, [canAccess, fetchSources]);
  useEffect(() => { if (canAccess) fetchEvents(); }, [canAccess, fetchEvents]);

  // PR_RUN_040 — fetch saved searches once on mount
  const fetchSaved = useCallback(async () => {
    try {
      const r = await fetch(`${API}/audit-explorer/saved-searches`, { credentials: "include" });
      if (r.ok) {
        const j = await r.json();
        setSavedSearches(j.items || []);
      }
    } catch (_) { /* non-fatal */ }
  }, []);
  useEffect(() => { if (canAccess) fetchSaved(); }, [canAccess, fetchSaved]);

  // PR_RUN_040 — current filter snapshot for save/load
  const currentFilters = useMemo(() => ({
    source: source || null,
    action: actionFilter.trim() || null,
    actor_id: actorId.trim() || null,
    entity_id: entityId.trim() || null,
    since: since || null,
    until: until || null,
    q: q.trim() || null,
  }), [source, actionFilter, actorId, entityId, since, until, q]);

  const applySavedSearch = (saved) => {
    const f = saved.filters || {};
    setSource(f.source || "");
    setActionFilter(f.action || "");
    setActorId(f.actor_id || "");
    setEntityId(f.entity_id || "");
    setSince((f.since || "").slice(0, 10));
    setUntil((f.until || "").slice(0, 10));
    setQ(f.q || "");
    setOffset(0);
  };

  const saveCurrentSearch = async () => {
    if (!saveName.trim()) return;
    const r = await fetch(`${API}/audit-explorer/saved-searches`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name: saveName.trim(), filters: currentFilters }),
    });
    if (r.ok) {
      setSaveName("");
      setSaveDialogOpen(false);
      fetchSaved();
    }
  };

  const deleteSavedSearch = async (search_id) => {
    const r = await fetch(`${API}/audit-explorer/saved-searches/${search_id}`, {
      method: "DELETE", credentials: "include",
    });
    if (r.ok) fetchSaved();
  };

  const downloadCsv = () => {
    setExporting(true);
    const params = new URLSearchParams();
    if (source) params.set("source", source);
    if (actionFilter.trim()) params.set("action", actionFilter.trim());
    if (actorId.trim()) params.set("actor_id", actorId.trim());
    if (entityId.trim()) params.set("entity_id", entityId.trim());
    if (since) params.set("since", `${since}T00:00:00+00:00`);
    if (until) params.set("until", `${until}T23:59:59+00:00`);
    if (q.trim()) params.set("q", q.trim());
    // Anchor-based download (server sets Content-Disposition).
    const url = `${API}/audit-explorer/events.csv?${params.toString()}`;
    // Use a temporary link so cookies (HttpOnly session) ride along.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => setExporting(false), 1500);
  };

  const clearFilters = () => {
    setSource("");
    setActionFilter("");
    setActorId("");
    setEntityId("");
    setSince("");
    setUntil("");
    setQ("");
    setOffset(0);
  };

  const activeFilterCount = useMemo(() => (
    (source ? 1 : 0) +
    (actionFilter.trim() ? 1 : 0) +
    (actorId.trim() ? 1 : 0) +
    (entityId.trim() ? 1 : 0) +
    (since ? 1 : 0) +
    (until ? 1 : 0) +
    (q.trim() ? 1 : 0)
  ), [source, actionFilter, actorId, entityId, since, until, q]);

  if (!canAccess) {
    return (
      <QMSLayout title="Audit-Trail Explorer">
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-8 text-center" data-testid="audit-explorer-forbidden">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <p className="text-white font-semibold mb-1">Access restricted</p>
          <p className="text-[#8B95A5] text-sm">
            The Audit-Trail Explorer is reserved for admin and auditor roles.
          </p>
        </div>
      </QMSLayout>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <QMSLayout title="Audit-Trail Explorer">
      <div className="space-y-4" data-testid="audit-explorer-root">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-['Syne'] text-2xl font-bold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#C9A84C]" />
              Audit-Trail Explorer
            </h2>
            <p className="text-[#8B95A5] text-xs mt-0.5">
              Unified view over the consolidated <span className="font-mono text-[#38BDF8]">operational_event_logs</span> collection
              {" "}(6 sources · {total.toLocaleString()} total events).
            </p>
          </div>
          <button
            onClick={() => fetchEvents()}
            className="px-3 py-1.5 rounded-md text-xs text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white hover:border-[#C9A84C]/40 transition-colors flex items-center gap-1.5"
            data-testid="audit-explorer-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {/* PR_RUN_040 — save current filter set */}
          <button
            onClick={() => setSaveDialogOpen(true)}
            disabled={activeFilterCount === 0}
            className="px-3 py-1.5 rounded-md text-xs text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white hover:border-[#C9A84C]/40 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="audit-explorer-save-btn"
            title={activeFilterCount === 0 ? "Apply at least one filter to save" : "Save current filter as a named search"}
          >
            <BookmarkPlus className="w-3 h-3" /> Save
          </button>
          {/* PR_RUN_040 — CSV export */}
          <button
            onClick={downloadCsv}
            disabled={exporting || total === 0}
            className="px-3 py-1.5 rounded-md text-xs text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white hover:border-[#C9A84C]/40 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="audit-explorer-csv-btn"
            title={total === 0 ? "No rows match current filters" : "Download up to 5000 rows as CSV"}
          >
            <Download className="w-3 h-3" /> CSV
          </button>
        </div>

      {/* PR_RUN_050 — Quick-pick presets (top of page).
          One-click shortcuts to the highest-traffic forensic queries.
          `audit_critical` is the structured-logger sub-stream that writes
          into `source=audit_log` — we pre-set the source chip and let the
          user narrow further if needed. */}
      <div
        className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-3 flex flex-wrap items-center gap-1.5"
        data-testid="audit-explorer-quickpicks"
      >
        <span className="text-[10px] uppercase tracking-widest text-[#8B95A5] mr-1.5">
          Quick picks
        </span>
        <button
          onClick={() => { setSource("trial"); setActionFilter(""); setQ(""); setOffset(0); }}
          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
            source === "trial" && !actionFilter
              ? "bg-[#C9A84C]/20 text-[#C9A84C] border-[#C9A84C]/40"
              : "text-[#8B95A5] border-[rgba(255,255,255,0.07)] hover:text-white hover:border-[#C9A84C]/30"
          }`}
          data-testid="audit-explorer-quickpick-trial"
          title="All trial-funnel events (signup → checkout → upgraded)"
        >
          Trial funnel
        </button>
        <button
          onClick={() => { setSource("audit_log"); setActionFilter(""); setQ(""); setOffset(0); }}
          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
            source === "audit_log"
              ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
              : "text-[#8B95A5] border-[rgba(255,255,255,0.07)] hover:text-white hover:border-amber-500/30"
          }`}
          data-testid="audit-explorer-quickpick-audit-critical"
          title="Critical audit stream (auth, RBAC, security events)"
        >
          Critical audit
        </button>
        <button
          onClick={() => { setSource("governance"); setActionFilter(""); setQ(""); setOffset(0); }}
          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
            source === "governance"
              ? "bg-sky-500/20 text-sky-300 border-sky-500/40"
              : "text-[#8B95A5] border-[rgba(255,255,255,0.07)] hover:text-white hover:border-sky-500/30"
          }`}
          data-testid="audit-explorer-quickpick-governance"
          title="Governance & assessment activity"
        >
          Governance
        </button>
        <button
          onClick={() => { setSource("remediation"); setActionFilter(""); setQ(""); setOffset(0); }}
          className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
            source === "remediation"
              ? "bg-red-500/20 text-red-300 border-red-500/40"
              : "text-[#8B95A5] border-[rgba(255,255,255,0.07)] hover:text-white hover:border-red-500/30"
          }`}
          data-testid="audit-explorer-quickpick-remediation"
          title="Remediation activity (CAPA actions, fixes)"
        >
          Remediation
        </button>
        <span className="ml-auto text-[10px] text-[#555]">
          One-click presets · clears keyword + action
        </span>
      </div>

      {/* PR_RUN_040 — Saved searches strip */}
      {savedSearches.length > 0 && (
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-3 flex flex-wrap items-center gap-1.5" data-testid="audit-explorer-saved-strip">
          <span className="text-[10px] uppercase tracking-widest text-[#8B95A5] mr-1.5 flex items-center gap-1">
            <Bookmark className="w-3 h-3 text-[#C9A84C]" /> Saved
          </span>
          {savedSearches.map((s) => (
            <div
              key={s.search_id}
              className="group flex items-center text-[11px] rounded-full border border-[rgba(255,255,255,0.07)] bg-[#0F1217] hover:border-[#C9A84C]/40 transition-colors"
              data-testid={`audit-explorer-saved-${s.search_id}`}
            >
              <button
                onClick={() => applySavedSearch(s)}
                className="px-2.5 py-1 text-[#8B95A5] hover:text-white"
              >
                {s.name}
              </button>
              <button
                onClick={() => deleteSavedSearch(s.search_id)}
                className="px-1.5 py-1 text-[#8B95A5] opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"
                aria-label={`Delete saved search ${s.name}`}
                data-testid={`audit-explorer-saved-delete-${s.search_id}`}
              >
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

        {/* Filter bar */}
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid="audit-explorer-filters">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-3.5 h-3.5 text-[#C9A84C]" />
            <span className="text-[11px] uppercase tracking-widest text-[#8B95A5] font-medium">Filters</span>
            {activeFilterCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#C9A84C]/15 text-[#C9A84C]">
                {activeFilterCount} active
              </span>
            )}
            <button
              onClick={clearFilters}
              className="ml-auto text-[11px] text-[#8B95A5] hover:text-white flex items-center gap-1"
              data-testid="audit-explorer-clear-filters"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </div>

          {/* Source chips */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="audit-explorer-source-chips">
            <button
              onClick={() => { setSource(""); setOffset(0); }}
              className={`px-2.5 py-1 text-[11px] rounded-full transition-colors ${
                !source
                  ? "bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C]/40"
                  : "text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white"
              }`}
              data-testid="audit-explorer-source-all"
            >
              All sources
            </button>
            {sources.map((src) => (
              <button
                key={src}
                onClick={() => { setSource(src); setOffset(0); }}
                className={`px-2.5 py-1 text-[11px] rounded-full transition-colors ${
                  source === src
                    ? "border"
                    : "text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white"
                }`}
                style={source === src ? {
                  color: SOURCE_META[src]?.color || "#8B95A5",
                  borderColor: `${SOURCE_META[src]?.color || "#8B95A5"}66`,
                  background: `${SOURCE_META[src]?.color || "#8B95A5"}15`,
                } : null}
                data-testid={`audit-explorer-source-${src}`}
              >
                {SOURCE_META[src]?.label || src}
              </button>
            ))}
          </div>

          {/* Text filter row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            <FilterInput
              testId="audit-explorer-action"
              placeholder="action (exact)"
              value={actionFilter}
              onChange={(v) => { setActionFilter(v); setOffset(0); }}
            />
            <FilterInput
              testId="audit-explorer-actor"
              placeholder="actor.user_id"
              value={actorId}
              onChange={(v) => { setActorId(v); setOffset(0); }}
            />
            <FilterInput
              testId="audit-explorer-entity"
              placeholder="entity.id"
              value={entityId}
              onChange={(v) => { setEntityId(v); setOffset(0); }}
            />
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={since}
                onChange={(e) => { setSince(e.target.value); setOffset(0); }}
                className="flex-1 px-2 py-1.5 rounded bg-[#0F1E35] border border-[#1E3A5F] text-[12px] text-slate-200 focus:outline-none focus:border-[#C9A84C]"
                data-testid="audit-explorer-since"
                aria-label="from date"
              />
              <span className="text-[#8B95A5] text-xs">→</span>
              <input
                type="date"
                value={until}
                onChange={(e) => { setUntil(e.target.value); setOffset(0); }}
                className="flex-1 px-2 py-1.5 rounded bg-[#0F1E35] border border-[#1E3A5F] text-[12px] text-slate-200 focus:outline-none focus:border-[#C9A84C]"
                data-testid="audit-explorer-until"
                aria-label="to date"
              />
            </div>
          </div>

          {/* PR_RUN_040 — payload keyword search */}
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOffset(0); }}
              placeholder="Keyword search across payload (details, notes, model_name, summary, …)"
              className="w-full pl-7 pr-7 py-1.5 bg-[#0F1E35] border border-[#1E3A5F] rounded text-[12px] text-slate-200 placeholder-slate-500 focus:outline-none focus:border-[#C9A84C]"
              data-testid="audit-explorer-q"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(""); setOffset(0); }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                aria-label="clear keyword"
                data-testid="audit-explorer-q-clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl overflow-hidden" data-testid="audit-explorer-results">
          <div className="px-4 py-2.5 border-b border-[rgba(255,255,255,0.05)] flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-[#8B95A5]">
              Events · <span className="text-white font-semibold">{total.toLocaleString()}</span> match
              {total !== 1 ? "es" : ""}
            </div>
            <div className="text-[11px] text-[#8B95A5]">
              page {currentPage} of {totalPages}
            </div>
          </div>

          {loading && (
            <div className="px-4 py-12 text-center text-[#8B95A5] text-sm" data-testid="audit-explorer-loading">
              <RefreshCw className="w-4 h-4 mx-auto mb-2 animate-spin" />
              Loading events…
            </div>
          )}

          {error && (
            <div className="px-4 py-6 text-center" data-testid="audit-explorer-error">
              <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-amber-400" />
              <p className="text-[#8B95A5] text-xs">Unable to load events: {error}</p>
            </div>
          )}

          {!loading && !error && events.length === 0 && (
            <div className="px-4 py-12 text-center" data-testid="audit-explorer-empty">
              <Search className="w-5 h-5 mx-auto mb-2 text-[#8B95A5] opacity-40" />
              <p className="text-[#8B95A5] text-sm">No events match the current filters.</p>
            </div>
          )}

          {!loading && !error && events.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]" data-testid="audit-explorer-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-[#8B95A5] border-b border-[rgba(255,255,255,0.05)]">
                    <th className="px-4 py-2 text-left font-medium">Timestamp</th>
                    <th className="px-4 py-2 text-left font-medium">Source</th>
                    <th className="px-4 py-2 text-left font-medium">Action</th>
                    <th className="px-4 py-2 text-left font-medium">Actor</th>
                    <th className="px-4 py-2 text-left font-medium">Entity</th>
                    <th className="px-4 py-2 text-right font-medium">Payload</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, idx) => (
                    <EventRow
                      key={ev.event_id || idx}
                      event={ev}
                      expanded={!!expanded[ev.event_id]}
                      onToggle={() => setExpanded((m) => ({ ...m, [ev.event_id]: !m[ev.event_id] }))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="px-4 py-2.5 border-t border-[rgba(255,255,255,0.05)] flex items-center justify-between text-[11px] text-[#8B95A5]">
            <div>
              showing {Math.min(offset + 1, total) || 0}–{Math.min(offset + events.length, total)} of {total.toLocaleString()}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || loading}
                className="px-2 py-1 rounded border border-[rgba(255,255,255,0.07)] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="audit-explorer-prev"
              >
                <ChevronLeft className="w-3 h-3" /> Prev
              </button>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total || loading}
                className="px-2 py-1 rounded border border-[rgba(255,255,255,0.07)] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="audit-explorer-next"
              >
                Next <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PR_RUN_040 — Save current search dialog */}
      {saveDialogOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setSaveDialogOpen(false)}
          data-testid="audit-explorer-save-dialog"
        >
          <div
            className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <BookmarkPlus className="w-4 h-4 text-[#C9A84C]" />
                Save current search
              </h3>
              <button onClick={() => setSaveDialogOpen(false)} className="text-[#8B95A5] hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[11px] text-[#8B95A5] mb-3">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied. The saved search will be visible only to you.
            </p>
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Governance — recent remediations"
              className="w-full px-3 py-2 rounded bg-[#0F1E35] border border-[#1E3A5F] text-[12px] text-slate-200 focus:outline-none focus:border-[#C9A84C] mb-3"
              data-testid="audit-explorer-save-name"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") saveCurrentSearch(); }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSaveDialogOpen(false)}
                className="px-3 py-1.5 rounded text-xs text-[#8B95A5] border border-[rgba(255,255,255,0.07)] hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={saveCurrentSearch}
                disabled={!saveName.trim()}
                className="px-3 py-1.5 rounded text-xs font-medium text-white bg-[#C9A84C] hover:bg-[#B8973B] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="audit-explorer-save-confirm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

const FilterInput = ({ testId, placeholder, value, onChange }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="px-2 py-1.5 rounded bg-[#0F1E35] border border-[#1E3A5F] text-[12px] text-slate-200 placeholder-slate-500 focus:outline-none focus:border-[#C9A84C] transition-colors"
    data-testid={testId}
  />
);

const EventRow = ({ event, expanded, onToggle }) => {
  const actor = event.actor || {};
  const entity = event.entity || {};
  const payloadStr = useMemo(() => {
    try { return JSON.stringify(event.payload || {}, null, 2); }
    catch { return String(event.payload || ""); }
  }, [event.payload]);

  const copyPayload = () => {
    try { navigator.clipboard.writeText(payloadStr); } catch { /* no-op */ }
  };

  return (
    <>
      <tr
        className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)] cursor-pointer"
        onClick={onToggle}
        data-testid={`audit-explorer-row-${event.event_id || ""}`}
      >
        <td className="px-4 py-2 text-slate-300 font-mono text-[11px] whitespace-nowrap">
          {fmtTimestamp(event.timestamp)}
        </td>
        <td className="px-4 py-2"><SourcePill source={event.source} /></td>
        <td className="px-4 py-2 text-slate-200 font-mono text-[11px]">{event.action || "—"}</td>
        <td className="px-4 py-2 text-slate-300 text-[11px]">
          {actor.user_id ? (
            <span title={actor.name || ""}>{actor.user_id}</span>
          ) : (
            <span className="text-[#8B95A5]">—</span>
          )}
        </td>
        <td className="px-4 py-2 text-slate-300 text-[11px]">
          {entity.id ? (
            <span><span className="text-[#8B95A5]">{entity.type}·</span>{entity.id}</span>
          ) : (
            <span className="text-[#8B95A5]">—</span>
          )}
        </td>
        <td className="px-4 py-2 text-right">
          <Eye className={`w-3.5 h-3.5 inline-block transition-opacity ${expanded ? "opacity-100 text-[#C9A84C]" : "opacity-40"}`} />
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#0F1217]">
          <td colSpan={6} className="px-4 py-3" data-testid={`audit-explorer-payload-${event.event_id || ""}`}>
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-[#8B95A5]">
                Payload · event_id <span className="font-mono text-slate-400">{event.event_id}</span>
                {event.legacy_collection && (
                  <span className="ml-2">
                    (migrated from <span className="font-mono text-slate-400">{event.legacy_collection}</span>)
                  </span>
                )}
              </div>
              <button
                onClick={copyPayload}
                className="text-[10px] text-[#8B95A5] hover:text-white flex items-center gap-1"
                data-testid={`audit-explorer-copy-${event.event_id || ""}`}
              >
                <Copy className="w-3 h-3" /> copy
              </button>
            </div>
            <pre className="text-[11px] text-slate-300 bg-[#0B0E14] border border-[rgba(255,255,255,0.05)] rounded p-2 overflow-x-auto font-mono whitespace-pre-wrap">
              {payloadStr}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
};

export default AuditExplorerPage;
